import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Paperclip, Loader2, Plus, Trash2, FileText } from "lucide-react";
import { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { startReconciliationAsync } from "@/Api/reconcialiationFinancialTransactions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PAYMENT_FORMS } from "@/utils/constants";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";

type FileItem = {
  id: string;
  paymentFormKey: string;
  paymentFormId: number;
  paymentFormName: string;
  file: File;
};

export const AddReconciliationFileBtn = ({
  id,
  updateList,
  itsPaidPayabled,
}: {
  id: string;
  updateList: any;
  itsPaidPayabled: boolean;
}) => {
  const PAYMENT_FORMS_LIST = [
    { key: "money", id: PAYMENT_FORMS.MONEY, name: "Dinheiro" },
    ...(itsPaidPayabled
      ? [{ key: "card", id: PAYMENT_FORMS.CREDIT_CARD, name: "Cartão" }]
      : []),
    { key: "pix_sicredi_ofx", id: PAYMENT_FORMS.PIX, name: "PIX Sicredi - OFX" },
    { key: "pix_btg_ofx", id: PAYMENT_FORMS.PIX_BTG, name: "PIX BTG - OFX" },
    {
      key: "pix_sicredi_excel",
      id: PAYMENT_FORMS.PIX,
      name: "PIX Sicredi - EXCEL",
    },
    {
      key: "pix_cnpj_excel",
      id: PAYMENT_FORMS.PIX_CNPJ,
      name: "PIX CNPJ - EXCEL",
    },
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileItems, setFileItems] = useState<FileItem[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedKey) return;

    const selectedForm = PAYMENT_FORMS_LIST.find((f) => f.key === selectedKey);
    if (!selectedForm) return;

    const newItems: FileItem[] = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      paymentFormKey: selectedForm.key,
      paymentFormId: selectedForm.id,
      paymentFormName: selectedForm.name,
      file: file,
    }));

    setFileItems((prev) => [...prev, ...newItems]);
    setSelectedKey(undefined);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveItem = (itemId: string) => {
    setFileItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const formSubmit = async () => {
    if (fileItems.length === 0) {
      toast.error("Adicione ao menos um arquivo");
      return;
    }

    setIsSubmitting(true);

    let successCount = 0;
    let errorCount = 0;

    for (const item of fileItems) {
      try {
        const { ok, data: apiResponse } = await startReconciliationAsync(id, {
          payment_form_id: item.paymentFormId,
          file: item.file,
        });

        if (ok) {
          successCount++;
        } else {
          errorCount++;
          toast.error(
            `Erro ao enviar ${item.file.name}: ${apiResponse?.message || "Erro desconhecido"}`
          );
        }
      } catch (error: any) {
        errorCount++;
        toast.error(
          `Erro ao enviar ${item.file.name}: ${error?.message || "Erro desconhecido"}`
        );
      }
    }

    setIsSubmitting(false);

    if (successCount > 0) {
      toast.success(
        `${successCount} arquivo(s) enviado(s) para processamento. Acompanhe o progresso abaixo.`
      );
      handleClose();
      updateList?.();
    }
  };

  const handleClose = () => {
    setSelectedKey(undefined);
    setFileItems([]);
    setIsOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    if (!selectedKey) {
      toast.error("Selecione a forma de pagamento primeiro");
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) {
          handleClose();
        } else if (open) {
          setIsOpen(true);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <Paperclip className="size-5" strokeWidth={1.75} />
          Fazer conciliação
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogTitle className="text-2xl">Fazer conciliação</DialogTitle>
          <DialogDescription className="text-lg">
            Adicione os arquivos com suas respectivas formas de pagamento
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="mb-2 block">Forma de Pagamento</Label>
              <Select
                onValueChange={setSelectedKey}
                value={selectedKey}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a forma de pagamento" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_FORMS_LIST.map((formItem) => (
                    <SelectItem key={formItem.key} value={formItem.key}>
                      {formItem.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={triggerFileInput}
              disabled={isSubmitting || !selectedKey}
              className="flex gap-1"
            >
              <Plus className="size-4" />
              Adicionar arquivo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleAddFile}
            />
          </div>

          {fileItems.length > 0 && (
            <div className="border rounded-lg p-3 space-y-2 max-h-[300px] overflow-y-auto">
              <Label className="text-sm text-muted-foreground">
                {fileItems.length} arquivo(s) adicionado(s)
              </Label>
              {fileItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="size-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {item.file.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.paymentFormName}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveItem(item.id)}
                    disabled={isSubmitting}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {fileItems.length === 0 && (
            <div className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground">
              <FileText className="size-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                Selecione a forma de pagamento e clique em "Adicionar arquivo"
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className={cn("", colors.bg_primary)}
            disabled={fileItems.length === 0 || isSubmitting}
            onClick={formSubmit}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              `Enviar ${fileItems.length > 0 ? `(${fileItems.length})` : ""}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
